<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="ml-2 mr-2">
      <b-row>
        <b-col
          cols="12"
          md="12"
        >
          <h5 class="section-label mb-2 mt-2">
            {{ $t('Settlement GL Setup') }}
          </h5>

          <!-- Table Top -->
          <b-row class="mb-2">

            <!-- Per Page -->
            <b-col
              cols="12"
              md="6"
              class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
            >
              <TableEntries
                v-model="perPage"
                :options="perPageOptions"
              />
              <Button
                v-if="getUserPermission('CONFIGURE_TRANSFER_GL')"
                :label="$t('Add')"
                size="md"
                @click="showModal('modal-lg')"
              />
            </b-col>

            <!-- Search -->
            <b-col
              cols="12"
              md="6"
            >
              <div class="d-flex align-items-center justify-content-lg-end">
                <Button
                  :label="$t('Filter')"
                  size="md"
                  icon="FilterIcon"
                  show-icon
                  class="mr-1"
                  @click="showModal('modal-filter')"
                />
                <Button
                  :label="$t('Reset Filter')"
                  size="md"
                  icon="RotateCwIcon"
                  show-icon
                  @click="resetFilter"
                />
              </div>
            </b-col>
          </b-row>

          <b-table
            ref="GatewayConfigsRef"
            :busy="isBusy"
            :per-page="pageSize"
            :current-page="currentPage"
            :items="settlementGlList"
            responsive
            :fields="translatedTableHeaders"
            primary-key="id"
            show-empty
            :empty-text="$t('No records available')"
            class="position-relative"
          >
            <template #table-busy>
              <div class="text-center text-danger my-2">
                <b-spinner
                  class="align-middle"
                  variant="primary"
                />
              </div>
            </template>
            <template #cell(SN)="data">
              {{ getOverallIndex(data.index) }}
            </template>

            <!-- Column: Actions -->
            <template #cell(actions)="data">

              <div class="text-nowrap">
                <!-- Dropdown -->
                <b-dropdown
                  variant="link"
                  toggle-class="p-0"
                  no-caret
                  :right="$store.state.appConfig.isRTL"
                >

                  <template #button-content>
                    <feather-icon
                      icon="MoreVerticalIcon"
                      size="16"
                      class="align-middle text-body"
                    />
                  </template>
                  <b-dropdown-item
                    v-if="getUserPermission('CONFIGURE_TRANSFER_GL')"
                    @click="onSelectGatewayConfigurationItem(data.item, $event.target, 'view')"
                  >
                    <feather-icon icon="EyeIcon" />
                    <span class="align-middle ml-50"> {{ $t('View') }} </span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="getUserPermission('CONFIGURE_TRANSFER_GL')"
                    @click="onSelectGatewayConfigurationItem(data.item, $event.target, 'edit')"
                  >
                    <feather-icon icon="EditIcon" />
                    <span class="align-middle ml-50"> {{ $t('Modify') }} </span>
                  </b-dropdown-item>
                </b-dropdown></div>
            </template>

          </b-table>

          <TablePagination
            v-model="currentPage"
            :from="from"
            :to="to"
            :total-rows="totalEntries"
            :per-page="perPage"
          />

          <!-- modal -->
          <b-modal
            id="modal-lg"
            ref="my-modal"
            no-close-on-backdrop
            :ok-title="buttonLabel"
            :cancel-title="$t('Cancel')"
            cancel-variant="outline-secondary"
            :ok-disabled="isLoading"
            centered
            size="lg"
            :title="modalTitle"
            @ok="handleSubmit"
            @hidden="resetModal"
          >
            <validation-observer ref="gatewayConfigRules">
              <b-form @submit.prevent="handleSubmit">
                <b-row>
                  <b-col
                    cols="12"
                    class="mb-0 mt-2"
                  >
                    <h5 class="section-label mb-2">
                      {{ $t('General Information') }}
                    </h5>
                  </b-col>
                </b-row>
                <b-row class="match-height">
                  <!-- Gateway Provider -->
                  <b-col md="6">
                    <SelectField
                      id="gateway-provider"
                      v-model="settlementGLFormData.gateway"
                      :label="$t('Gateway Provider')"
                      :options="gatewayProviderOptions"
                      :error-name="$t('Gateway Provider')"
                      rules="required"
                      item="label"
                      :reduce="option => option"
                      :placeholder="$t('Select')"
                      :readonly="isReadonly"
                    />

                  </b-col>
                  <!-- Gateway Provider -->
                  <!-- Inward Transfers Settlement GL -->
                  <b-col md="6">
                    <InputField
                      id="inward-transfers-settlement-gl"
                      v-model="settlementGLFormData.inwardSettlementGl"
                      :label="$t('Inward Transfers Settlement GL')"
                      :placeholder="$t('Enter inward settlement GL')"
                      type="number"
                      :error-name="$t('Inward Transfers Settlement GL')"
                      rules="required"
                      for="inward-transfers-settlement-gl"
                      :readonly="isReadonly"
                    />
                  </b-col>
                  <!-- Inward Transfers Settlement GL -->
                  <!-- Outward Transfers Settlement GL -->
                  <b-col md="6">
                    <InputField
                      id="outward-transfers-settlement-gl"
                      v-model="settlementGLFormData.outwardSettlementGl"
                      :label="$t('Outward Transfers Settlement GL')"
                      :placeholder="$t('Enter outward settlement GL')"
                      type="number"
                      :error-name="$t('Outward Transfers Settlement GL')"
                      rules="required"
                      for="outward-transfers-settlement-gl"
                      :readonly="isReadonly"
                    />
                  </b-col>
                  <!-- Outward Transfers Settlement GL -->

                  <!-- Outward Transfers Expense GL -->
                  <b-col md="6">
                    <InputField
                      id="outward-transfers-expense-gl"
                      v-model="settlementGLFormData.outwardExpenseGl"
                      :label="$t('Outward Transfers Expense GL')"
                      :placeholder="$t('Enter outward Expense GL')"
                      type="number"
                      :error-name="$t('Outward Transfers Expense GL')"
                      rules="required"
                      for="outward-transfers-expense-gl"
                      :readonly="isReadonly"
                    />
                  </b-col>
                  <!-- Outward Transfers Expense GL -->
                </b-row>
              </b-form>
            </validation-observer>

            <template
              v-if="isReadonly"
              #modal-footer="{ }"
            >
              <div />
            </template>
          </b-modal>
          <!-- modal -->
          <!-- Filter modal -->
          <b-modal
            id="modal-filter"
            ref="my-modal"
            :ok-title="$t('Apply')"
            :cancel-title="$t('Cancel')"
            cancel-variant="outline-secondary"
            no-close-on-backdrop
            centered
            size="lg"
            :title="$t('Filters')"
            modal-footer
            @ok="fetchGatewayConfiguration"
            @cancel="resetFilters"
          >
            <b-row class="match-height">
              <!-- Gateway -->
              <b-col md="6">
                <SelectField
                  id="gateway"
                  v-model="filter.gateway"
                  :label="$t('Gateway')"
                  :reduce="option=>option"
                  :placeholder="$t('Select')"
                  :options="gatewayProviderOptions"
                  item="label"
                />
              </b-col>
              <!-- Gateway -->
            </b-row>
          </b-modal>
        </b-col></b-row></div>
    <!-- Filter modal -->
    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BModal,
  BForm, BDropdown, BDropdownItem, VBTooltip,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { mapActions } from 'vuex'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import {
  required, amount, positive,
} from '@validations'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../../../forms/InputField.vue'
import SelectField from '../../../../forms/SelectField.vue'
import { toDateWithYear } from '../../../../../helpers/formatDate'
import getStatusColor from '../../../../../helpers/getStatusColor'
import Button from '../../../../components/button/Button.vue'
import { eChannelsPermission } from '../../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BSpinner,
    BModal,
    BForm,
    ValidationObserver,
    BDropdown,
    BDropdownItem,
    InputField,
    SelectField,
    TablePagination,
    FullScreenSpinner,
    Button,
    TableEntries,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toDateWithYear,
      showSpinner: false,
      isDisabled: false,
      isCustomDisabled: false,
      isLoading: false,
      required,
      amount,
      positive,
      getStatusColor,
      modalTitle: this.$t('Settlement GL Setup'),
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'gateway', sortable: true, label: 'Gateway Provider' },
        { key: 'inwardSettlementGl', sortable: true, label: 'Inward Settlement GL' },
        { key: 'outwardSettlementGl', sortable: true, label: 'Outward Settlement GL' },
        { key: 'outwardExpenseGl', sortable: true, label: 'Outward Expense GL' },
        { key: 'actions' },
      ],
      settlementGLFormData: {
        gateway: '',
        inwardSettlementGl: '',
        outwardSettlementGl: '',
        outwardExpenseGl: '',
      },
      gatewayProviderOptions: [],
      settlementGlList: [],
      filter: {
        gateway: '',
      },
    }
  },
  computed: {
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchGatewayConfiguration()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchGatewayConfiguration()
    },

  },
  mounted() {
    this.fetchGatewayConfiguration()
    this.fetchGatewayProviders()
  },
  methods: {
    ...mapActions({
      getGatewayConfigurations: 'eChannels/getGatewayConfigurations',
      createGatewayConfiguration: 'eChannels/createGatewayConfigurationAction',
      updateGatewayConfiguration: 'eChannels/updateGatewayConfigurationAction',
      getGatewayProviders: 'eChannels/getGatewayProviders',
    }),
    handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.gatewayConfigRules.validate().then(async success => {
        if (success) {
          this.confirm(this.gatewayConfigAction, { text: `Are you sure you want to ${this.buttonLabel} this Gateway Configuration?` })
        }
      })
    },
    async gatewayConfigAction() {
      try {
        this.isLoading = true
        const formData = {
          ...this.settlementGLFormData,
        }
        const response = this.buttonLabel === 'Save'
          ? await this.createGatewayConfiguration(formData)
          : await this.updateGatewayConfiguration(formData)

        const { status } = await response
        if (status === 200) {
          this.fetchGatewayConfiguration()
          this.hideModal('modal-lg')
          const requestText = this.$t('Your request to')
          const successText = this.$t('Gateway Configuration was successful')
          this.alertSuccess({ text: `${requestText} ${this.buttonLabel} ${successText}` })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const requestText = this.$t('Your request to')
          const reasonText = this.$t('Gateway Configuration failed. Reason')
          const errorMessage = `${requestText} ${this.buttonLabel} ${reasonText}: ${responseError.errorMessage}`
          this.alertError({ text: errorMessage })
        })
      } finally {
        this.isLoading = false
      }
    },
    onSelectGatewayConfigurationItem(items, activity, val) {
      try {
        this.isDisabled = val !== 'edit'
        this.isCustomDisabled = val === 'view' || val === 'edit'
        this.settlementGLFormData = { ...items }
        this.isReadonly = val === 'edit' ? this.isReadonly = false : this.isReadonly = true
        this.modalTitle = val === 'edit' ? this.$t('Edit Gateway Configuration') : this.$t('View Gateway Configuration')
        this.buttonLabel = this.$t('Update')
        this.$root.$emit('bv::show::modal', 'modal-lg')
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async fetchGatewayConfiguration() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getGatewayConfigurations({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: eChannelsPermission.CONFIGURE_TRANSFER_GL,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.settlementGlList = data.data
          this.totalCount = data.recordCount
        } else {
          this.settlementGlList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async fetchGatewayProviders() {
      try {
        const response = await this.getGatewayProviders({ permission: eChannelsPermission.CONFIGURE_TRANSFER_GL })
        const { status, data } = await response
        if (status === 200 && data) {
          this.gatewayProviderOptions = data
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    resetModal() {
      this.settlementGLFormData = { ...null }
      this.isReadonly = false
      this.isLoading = false
      this.isDisabled = false
      this.isCustomDisabled = false
      this.modalTitle = this.$t('Create Gateway Configuration')
      this.buttonLabel = this.$t('Save')
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchGatewayConfiguration()
    },
  },
}
</script>
